import {
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { FOCUSABLE_SELECTOR } from "./Dialog.tsx";

/**
 * Portals a dropdown into document.body instead of rendering it inline: a
 * popover rendered inside `.topbar` (its own stacking context) is capped at
 * that context's rank regardless of its own local z-index, so Leaflet's
 * panes paint over it even though hit-testing still says it's on top.
 * Screen position is computed here from `anchorRef`'s own rect since a
 * portaled element can't inherit position from a `position: relative`
 * ancestor. `popoverRef` is the caller's own ref, not one owned here, so its
 * `useClickOutside` can treat `[triggerRef, popoverRef]` as one region —
 * otherwise a click inside the portaled content would register as "outside"
 * the trigger and close it immediately.
 */
export function Popover({
  anchorRef,
  popoverRef,
  align = "right",
  className,
  onClose,
  children,
}: {
  anchorRef: RefObject<HTMLElement>;
  popoverRef: RefObject<HTMLDivElement>;
  /** Which edge of the trigger this popover's own edge lines up with. */
  align?: "left" | "right";
  className?: string;
  /**
   * Called on Escape. Also moves focus to the popover's first focusable
   * element once rendered, and restores it to the trigger on unmount —
   * without this a portaled dropdown leaves a keyboard user's focus
   * stranded on the trigger while the menu renders elsewhere in the DOM.
   * Not a full focus trap: Tab still leaves the menu after its last item.
   */
  onClose: () => void;
  children: ReactNode;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    setRect(anchorRef.current?.getBoundingClientRect() ?? null);
  }, [anchorRef]);

  // Recomputes on resize/scroll so the portaled dropdown keeps following its
  // trigger. `scroll` is captured (not bubbled) to catch a scroll on any
  // ancestor element, not just the window.
  useEffect(() => {
    function recompute() {
      setRect(anchorRef.current?.getBoundingClientRect() ?? null);
    }
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Only steals focus into the popover's first item when the trigger
  // already has keyboard focus at the moment this opens (Tab, then
  // Enter/Space) — not for a plain mouse click, which already put the
  // pointer where the user wants it. Depends on `hasRect` (a boolean that
  // flips false->true exactly once, the instant `rect` first becomes
  // non-null), NOT `rect` itself: `rect` gets a brand new object identity on
  // every later resize/scroll recompute, and depending on that directly
  // reran this effect's own cleanup (`previouslyFocused.focus()`) followed
  // immediately by a fresh steal on every single scroll tick — visibly
  // yanking focus back to the popover's first item mid-scroll, even after
  // the user had already tabbed to a later one. `hasRect` only ever
  // transitions once, so the effect runs exactly once too (with the
  // now-current `rect`/`popoverRef.current`, since this passive effect only
  // fires once React settles on the render where `hasRect` actually flipped).
  const hasRect = rect !== null;
  useEffect(() => {
    if (!hasRect) return;
    if (!anchorRef.current?.contains(document.activeElement)) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = popoverRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusable?.[0]?.focus();
    return () => previouslyFocused?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRect]);

  if (!rect) return null;

  const style: CSSProperties = {
    position: "fixed",
    top: rect.bottom + 8,
    ...(align === "left" ? { left: rect.left } : { right: window.innerWidth - rect.right }),
  };

  return createPortal(
    <div ref={popoverRef} className={className} style={style}>
      {children}
    </div>,
    document.body
  );
}
